<template lang="pug">
.span(v-if="display") {{ number !== null ? number : "-" }} {{ number !== null ? $t(unit, number) : "" }}
.flex(v-else)
  el-input-number.mr-4.min-w-24(
    v-model="number",
    :min="allowNull ? 0 : 1",
    :max="99",
    :disabled="disabled"
  )
  .w-32
    el-select(v-if="number > 0", v-model="unit", :disabled="disabled")
      el-option(
        v-for="o in unitOptions",
        :key="o",
        :value="o",
        :label="$t(o, number)"
      )
</template>

<script lang="ts" setup>
import { onMounted } from "vue";
import { watch } from "vue";
import { ref } from "vue";

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    // Only display the value
    display?: boolean;
    allowNull?: boolean;
    disabled?: boolean;
    unitOptions?: string[];
  }>(),
  {
    modelValue: null,
    unitOptions: () => ["days", "weeks", "months", "years"],
  },
);

const emit = defineEmits(["update:modelValue"]);

const number = ref<number>(null);

const unit = ref(props.unitOptions[0]);

function emitDuration() {
  if (
    unit.value === null ||
    unit.value === undefined ||
    number.value === null
  ) {
    emit("update:modelValue", null);
  } else {
    if (props.allowNull && number.value === 0) {
      emit("update:modelValue", `PT0S`);
    } else {
      if (["days", "weeks", "months", "years"].includes(unit.value)) {
        emit(
          "update:modelValue",
          `P${number.value}${unit.value.charAt(0).toUpperCase()}`,
        );
      } else {
        emit(
          "update:modelValue",
          `PT${number.value}${unit.value.charAt(0).toUpperCase()}`,
        );
      }
    }
  }
}

watch(number, emitDuration);
watch(unit, emitDuration);

const nr = "\\d+(?:[\\.,]\\d+)?";
const dateRegex = `(${nr}Y)?(${nr}M)?(${nr}W)?(${nr}D)?`;
const timeRegex = `T(${nr}H)?(${nr}M)?(${nr}S)?`;
const durationRegex = new RegExp(`P${dateRegex}(?:${timeRegex})?`);
const parsePart = (val: string | null) => (val ? parseFloat(val) : 0);

function setDuration() {
  if (props.modelValue) {
    if (props.modelValue === "PT0S" && props.allowNull) {
      number.value = 0;
      return;
    }
    try {
      // Parsing durations which have only one number
      const match = props.modelValue.match(durationRegex);

      const years = parsePart(match[1]);
      const months = parsePart(match[2]);
      const weeks = parsePart(match[3]);
      const days = parsePart(match[4]);

      const hours = parsePart(match[5]);
      const minutes = parsePart(match[6]);
      const seconds = parsePart(match[7]);

      // NOTE: we only support years, months, weeks, days, hours, minutes, seconds
      // and only one of them set. Fallback is 0 days, note that this will be stored as `PT0S` in the backend
      number.value =
        years || months || weeks || days || hours || minutes || seconds || 0;
      const u = `${years ? "Y" : months ? "M" : weeks ? "W" : days ? "D" : "D"}`;

      unit.value = years
        ? "years"
        : months
          ? "months"
          : weeks
            ? "weeks"
            : days
              ? "days"
              : hours
                ? "hours"
                : minutes
                  ? "minutes"
                  : "seconds";
    } catch (e) {
      console.error("Error parsing duration: ", props.modelValue);
      // Sentry.captureException(e);
    }
  } else {
    number.value = null;
  }
}

onMounted(setDuration);
watch(() => props.modelValue, setDuration);
</script>
